import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { LineChart, Mail } from 'lucide-react'
import { fetchAdminEmailMetrics, type EmailMarketingMetricsResponse } from '../api/admin'
import { useAuth } from '../hooks/use-auth'
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'

const StatCard: React.FC<{ label: string; value: string | number; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="rounded-xl border border-secondary-200 bg-background p-4">
    <div className="flex items-center justify-between">
      <p className="text-sm text-foreground/60">{label}</p>
      {icon}
    </div>
    <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
  </div>
)

const GrowthChart: React.FC<{ data: Array<{ date: string; count: number }>; title: string; colorVar: string; gradientId: string }> = ({ data, title, colorVar, gradientId }) => {
  const chartData = data.map(d => ({
    label: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    count: d.count,
  }))

  return (
    <div className="h-40">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={colorVar} stopOpacity={0.3} />
              <stop offset="95%" stopColor={colorVar} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-secondary-200)" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'rgba(23,23,23,0.6)' }} axisLine={false} tickLine={false} interval={4} />
          <YAxis tick={{ fontSize: 11, fill: 'rgba(23,23,23,0.6)' }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
          <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid var(--color-secondary-200)' }} labelStyle={{ color: 'var(--color-foreground)' }} formatter={(v: number) => [v, title]} />
          <Area type="monotone" dataKey="count" stroke={colorVar} strokeWidth={2} fill={`url(#${gradientId})`} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

const AdminEmailPage: React.FC = () => {
  const { user } = useAuth()

  const metricsQuery = useQuery<EmailMarketingMetricsResponse, Error>({
    queryKey: ['admin', 'email-metrics'],
    queryFn: fetchAdminEmailMetrics,
    enabled: !!user?.isAdmin,
    staleTime: 60_000,
  })

  if (!user?.isAdmin) {
    return (
      <div className="rounded-xl border border-secondary-200 bg-background p-6">
        <p className="text-foreground">You do not have access to this page.</p>
      </div>
    )
  }

  const subs = metricsQuery.data?.subscribedUsers ?? 0
  const unsub = metricsQuery.data?.unsubscribedUsers ?? 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Email marketing</h1>
      </div>

      {/* Top-level stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Subscribers" value={subs} icon={<Mail className="h-4 w-4 text-foreground/40" />} />
        <StatCard label="Unsubscribed" value={unsub} />
        <StatCard label="Net list size" value={subs - unsub} />
      </div>

      {/* Growth charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-foreground">Subscriber growth</h2>
            <LineChart className="h-4 w-4 text-foreground/40" />
          </div>
          <div className="rounded-xl border border-secondary-200 bg-background p-4">
            {metricsQuery.isLoading ? (
              <div className="h-40 rounded bg-secondary-50 animate-pulse" />
            ) : (
              <GrowthChart 
                data={(metricsQuery.data?.subscribersGrowthLast30Days ?? []).map(d => ({ date: new Date(d.date).toISOString(), count: d.count }))}
                title="Subscribers"
                colorVar="var(--color-brand-600)"
                gradientId="subsGrowth"
              />
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-foreground">Unsubscribes</h2>
            <LineChart className="h-4 w-4 text-foreground/40" />
          </div>
          <div className="rounded-xl border border-secondary-200 bg-background p-4">
            {metricsQuery.isLoading ? (
              <div className="h-40 rounded bg-secondary-50 animate-pulse" />
            ) : (
              <GrowthChart 
                data={(metricsQuery.data?.unsubscribesLast30Days ?? []).map(d => ({ date: new Date(d.date).toISOString(), count: d.count }))}
                title="Unsubscribes"
                colorVar="var(--color-danger-600)"
                gradientId="unsubsGrowth"
              />
            )}
          </div>
        </div>
      </div>

      {/* Per-email-type table */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Lifecycle emails</h2>
        <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-background">
          <table className="w-full text-sm">
            <thead className="text-left text-foreground/60">
              <tr>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Trigger</th>
                <th className="px-4 py-3">Sent</th>
              </tr>
            </thead>
            <tbody>
              {(metricsQuery.data?.emailTypes ?? []).map((t) => (
                <tr key={t.type} className="border-t border-secondary-100">
                  <td className="px-4 py-3 font-medium text-foreground">{t.type}</td>
                  <td className="px-4 py-3 text-foreground/70">{t.trigger}</td>
                  <td className="px-4 py-3">{t.sentCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default AdminEmailPage
